import { Box, Typography } from '@mui/material';
import { useState, type ReactElement } from 'react';

import { createAdminApi, type AdminPrivacyRequest, type PrivacyRequestType } from '@/admin/adminApi';
import { messageOf } from '@/admin/users/adminErrors';
import { useHttpClient } from '@/app/providers';
import { Button, Dialog, Field, SelectField } from '@/ui/kit';

import { InfoNote } from './InfoNote';
import { TYPE_LABELS, TYPE_ORDER, hasErrors, validateNewRequest, type NewRequestErrors } from './privacyRequestsModel';

/**
 * « Nouvelle demande » : saisir une demande recue **hors de l'application**
 * (courriel, courrier).
 *
 * Le dialogue ne calcule rien : l'echeance est celle que le serveur rend a
 * l'enregistrement, et l'ecran la reaffiche. La date future est refusee **ici**
 * (message en clair sous le champ) et **la-bas** (422) : le serveur reste le seul
 * juge. Un echec n'efface pas la saisie.
 *
 * Il est monte a l'ouverture seulement : un formulaire neuf a chaque fois, jamais
 * l'adresse de la demande precedente.
 */
export const NewRequestDialog = ({
  onClose,
  onCreated,
}: {
  readonly onClose: () => void;
  readonly onCreated: (created: AdminPrivacyRequest) => void;
}): ReactElement => {
  const http = useHttpClient();
  const [type, setType] = useState<PrivacyRequestType>('access');
  const [email, setEmail] = useState('');
  const [receivedOn, setReceivedOn] = useState('');
  const [errors, setErrors] = useState<NewRequestErrors>({});
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const found = validateNewRequest({ type, email, receivedOn }, new Date());
    setErrors(found);
    setFailure(null);

    if (hasErrors(found)) {
      return;
    }

    setPending(true);

    try {
      onCreated(await createAdminApi(http).createPrivacyRequest({ type, requesterEmail: email.trim(), receivedAt: receivedOn }));
    } catch (error: unknown) {
      setFailure(messageOf(error));
      setPending(false);
    }
  };

  return (
    <Dialog
      open
      onClose={pending ? undefined : onClose}
      title="Nouvelle demande"
      description="Enregistre une demande reçue par courriel ou par courrier. Le registre ne la reçoit pas tout seul."
      actions={
        <>
          <Button onClick={onClose} disabled={pending}>
            Annuler
          </Button>
          <Button
            variant="primary"
            disabled={pending}
            onClick={() => {
              void submit();
            }}
          >
            {pending ? 'Enregistrement…' : 'Enregistrer la demande'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
        <SelectField
          label="Droit exercé"
          required
          value={type}
          onChange={(value) => {
            setType(value as PrivacyRequestType);
          }}
          options={TYPE_ORDER.map((value) => ({ value, label: TYPE_LABELS[value] }))}
        />
        <Field
          label="Adresse e-mail du demandeur"
          required
          type="email"
          autoComplete="off"
          value={email}
          onChange={setEmail}
          error={errors.email}
          hint="Le compte actif qui porte cette adresse est retrouvé et relié à la demande ; sans compte, la demande reste valable."
        />
        <Field
          label="Date de réception"
          required
          type="date"
          value={receivedOn}
          onChange={setReceivedOn}
          error={errors.receivedOn}
          hint="L’échéance légale (un mois, art. 12.3 du RGPD) est calculée par le serveur à l’enregistrement."
        />
        <InfoNote>
          L’enregistrement n’exécute rien : l’accès, l’export, la rectification et l’anonymisation se font depuis la fiche du compte.
        </InfoNote>
        {failure === null ? null : (
          <Typography role="alert" sx={{ fontSize: 14, color: 'error.main' }}>
            {failure}
          </Typography>
        )}
      </Box>
    </Dialog>
  );
};
